import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '@/components/ui/custom/Button';
import Icon from '@/components/AppIcon';
import { useAssistant } from '@/contexts/AssistantContext';
import { formatStatusLabel } from '@/contexts/assistantThreadStatus';
import { cn } from '@/utils/cn';
import AssistantMarkdown from './AssistantMarkdown';
import ProposalCard from './ProposalCard';

const formatCents = (cents) => {
  const value = Number(cents);
  if (!Number.isFinite(value)) return null;
  return `$${(value / 100).toFixed(2)}`;
};

export const extractQuoteItems = (quotePayload) => {
  // Prefer the cart the assistant just built — it carries real item names and
  // member assignments (the Phantom order payload only has opaque item ids).
  // A line for a member with an allergy or diet carries it and the line's
  // choices, so a conflicting pick is on the card.
  if (Array.isArray(quotePayload?.cart_items) && quotePayload.cart_items.length > 0) {
    return quotePayload.cart_items.map((item, index) => ({
      key: `cart-item-${index}`,
      name: item?.name || 'Menu item',
      quantity: Number(item?.quantity) > 0 ? Number(item.quantity) : 1,
      memberName: item?.member_name || null,
      allergies: typeof item?.allergies === 'string' && item.allergies.trim() ? item.allergies.trim() : null,
      choices: Array.isArray(item?.choices) ? item.choices.filter(Boolean) : [],
      dietaryTags: [],
    }));
  }
  const items =
    quotePayload?.input?.order?.items ||
    quotePayload?.input?.order?.line_items ||
    quotePayload?.result?.items ||
    [];
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => ({
    key: item?.id || item?.item_id || `item-${index}`,
    name: item?.name || item?.title || item?.item_name || 'Menu item',
    quantity: Number(item?.quantity) > 0 ? Number(item.quantity) : 1,
    memberName: null,
    allergies: null,
    choices: [],
    dietaryTags: [
      ...(Array.isArray(item?.dietary_tags) ? item.dietary_tags : []),
      ...(Array.isArray(item?.allergens) ? item.allergens : []),
      ...(Array.isArray(item?.dietaryFlags) ? item.dietaryFlags : []),
    ],
  }));
};

const extractQuoteRestaurantName = (quotePayload) =>
  quotePayload?.restaurant_name ||
  quotePayload?.result?.restaurantName ||
  quotePayload?.result?.restaurant_name ||
  quotePayload?.input?.order?.restaurant_name ||
  quotePayload?.input?.restaurant_id ||
  'Restaurant';

const extractQuoteDietaryFlags = (quotePayload) => {
  const topLevel = quotePayload?.result?.dietaryFlags || quotePayload?.result?.dietary_flags;
  const flags = new Set(Array.isArray(topLevel) ? topLevel.map((flag) => String(flag)) : []);
  for (const item of extractQuoteItems(quotePayload)) {
    for (const tag of item.dietaryTags) flags.add(String(tag));
  }
  return [...flags];
};

// The quote prices whatever tip the assistant put on the order — usually none.
// Calling that figure "Total" read as the amount the coach would be charged.
export const quoteTotalLabel = (quotePayload) => {
  const tipCents = Number(quotePayload?.quote?.tip_cents ?? quotePayload?.result?.tipCents ?? 0);
  return Number.isFinite(tipCents) && tipCents > 0 ? 'Estimated total' : 'Estimated total before tip';
};

// A quote the assistant ran without building a cart: nothing to place from it.
export const isPriceCheckQuote = (quotePayload) => quotePayload?.price_check === true || !quotePayload?.cart_id;

const QuoteCard = ({ quotePayload, dismissed, ordered, onConfirm, onCancel, onBuild, onViewOrders }) => {
  const items = extractQuoteItems(quotePayload);
  const dietaryFlags = extractQuoteDietaryFlags(quotePayload);
  const total =
    formatCents(quotePayload?.quote?.total_with_tip_cents) ||
    formatCents(quotePayload?.result?.totalCents);
  const totalLabel = quoteTotalLabel(quotePayload);
  const priceCheck = isPriceCheckQuote(quotePayload);

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <Icon name="ReceiptText" size={16} />
        <span>
          {priceCheck ? 'Price check' : 'Quote'} — {extractQuoteRestaurantName(quotePayload)}
        </span>
      </div>
      {priceCheck && <p className="mt-1 text-xs text-muted-foreground">No cart was built for this price.</p>}

      {items.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm text-foreground">
          {items.map((item) => (
            <li key={item.key}>
              <div className="flex justify-between gap-4">
                <span>
                  {item.quantity > 1 ? `${item.quantity}× ` : ''}
                  {item.name}
                </span>
                {item.memberName ? (
                  <span className="text-xs text-muted-foreground">{item.memberName}</span>
                ) : item.dietaryTags.length > 0 ? (
                  <span className="text-xs text-muted-foreground">{item.dietaryTags.join(', ')}</span>
                ) : null}
              </div>
              {item.allergies && (
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-warning">Allergic to: {item.allergies}</span>
                  {item.choices.length > 0 ? ` · ${item.choices.join(', ')}` : ''}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {dietaryFlags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {dietaryFlags.map((flag) => (
            <span
              key={flag}
              className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            >
              {flag}
            </span>
          ))}
        </div>
      )}

      {total && (
        <div className="mt-3 border-t border-border pt-3">
          <div className="flex justify-between text-sm font-semibold text-foreground">
            <span>{totalLabel}</span>
            <span>{total}</span>
          </div>
          {/* This is the assistant's pricing of the cart, not the charge: the
              tip is chosen at checkout, and a large order is placed as several
              provider orders, each with its own fees. */}
          <p className="mt-1 text-xs text-muted-foreground">
            Estimate — the tip and final fees are confirmed at checkout.
          </p>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {dismissed ? (
          <p className="text-sm text-muted-foreground">Quote dismissed.</p>
        ) : ordered ? (
          <>
            <p className="text-sm text-muted-foreground">Already ordered.</p>
            <Button size="sm" variant="outline" onClick={onViewOrders}>
              View orders
            </Button>
          </>
        ) : priceCheck ? (
          <>
            <Button size="sm" iconName="ShoppingCart" onClick={onBuild}>
              Build this cart
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>
              Dismiss
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" iconName="ArrowRight" iconPosition="right" onClick={onConfirm}>
              Review &amp; place order
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const RestaurantCards = ({ restaurants, onOpen }) => (
  <div className="space-y-2">
    {restaurants.map((restaurant, index) => (
      <button
        key={restaurant?.id || index}
        type="button"
        onClick={() => restaurant?.path && onOpen(restaurant.path)}
        className="flex w-full items-center gap-3 rounded-lg border border-border bg-card p-2.5 text-left shadow-sm transition-athletic hover:border-primary/40 hover:bg-muted"
      >
        {restaurant?.image_url ? (
          <img
            src={restaurant.image_url}
            alt=""
            loading="lazy"
            className="h-16 w-16 flex-shrink-0 rounded-md object-cover bg-muted"
            onError={(event) => {
              event.currentTarget.style.visibility = 'hidden';
            }}
          />
        ) : (
          <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-md bg-muted">
            <Icon name="UtensilsCrossed" size={20} className="text-muted-foreground" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-semibold text-foreground">{restaurant?.name}</span>
            {restaurant?.eta_text && (
              <span className="flex-shrink-0 text-xs text-muted-foreground">{restaurant.eta_text}</span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            {Number.isFinite(Number(restaurant?.rating)) && (
              <span className="flex items-center gap-0.5">
                <Icon name="Star" size={12} className="text-warning fill-warning" />
                <span className="font-medium text-foreground">{Number(restaurant.rating).toFixed(1)}</span>
                {Number.isFinite(Number(restaurant?.review_count)) && (
                  <span>({Number(restaurant.review_count).toLocaleString()})</span>
                )}
              </span>
            )}
            {Number.isFinite(Number(restaurant?.distance_miles)) && (
              <span>{restaurant.distance_miles} mi</span>
            )}
            {restaurant?.cuisine && <span className="truncate">{restaurant.cuisine}</span>}
          </div>
          {/* No address line here by design — the card stays compact; the
              address shows on the detail page and at checkout. */}
        </div>
        <Icon name="ChevronRight" size={16} className="flex-shrink-0 text-muted-foreground" />
      </button>
    ))}
  </div>
);

// How close to the bottom (px) still counts as reading the newest entry.
const PINNED_SLACK_PX = 48;

const AssistantChat = ({ className = '', inputRef = null }) => {
  const { thread, sending, sendMessage, confirmQuote, cancelQuote, confirmProposal, cancelProposal, orgType, goTo, viewCart } =
    useAssistant();
  const [input, setInput] = useState('');
  const scrollRef = useRef(null);
  // Follow new entries only while the coach is at the bottom: every streamed
  // delta rebuilds the thread, and scrolling up to read an earlier reply was
  // pulled back down on the next one. Their own new message always jumps.
  const pinnedRef = useRef(true);
  const handleScroll = () => {
    const el = scrollRef.current;
    if (el) pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= PINNED_SLACK_PX;
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (thread[thread.length - 1]?.type === 'user') pinnedRef.current = true;
    // Instant, not smooth: a smooth scroll's in-between positions read as
    // the coach scrolling away, and the stream stopped being followed.
    if (pinnedRef.current) el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
  }, [thread]);

  // Only the newest tool line of a reply still running is in progress;
  // earlier ones are done (they kept spinning through later turns).
  const activeStatusId =
    sending && thread[thread.length - 1]?.type === 'status' && thread[thread.length - 1]?.transient
      ? thread[thread.length - 1].id
      : null;

  const handleSubmit = (event) => {
    event?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    void sendMessage(text);
  };

  const emptyState = useMemo(
    () => (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Icon name="ChefHat" size={32} className="text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">
          Tell me what your team needs — e.g. “Find dinner near our hotel for 7pm Friday.”
        </p>
      </div>
    ),
    [],
  );

  return (
    <div className={cn('flex flex-col', className)}>
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 min-h-0 space-y-3 overflow-y-auto px-3 pb-4 pt-3">
        {thread.length === 0 && emptyState}
        {thread.map((entry) => {
          if (entry.type === 'user') {
            return (
              <div key={entry.id} className="flex justify-end">
                <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-primary px-4 py-2 text-sm text-primary-foreground whitespace-pre-wrap">
                  {entry.text}
                </div>
              </div>
            );
          }
          if (entry.type === 'assistant') {
            return (
              <div key={entry.id} className="flex justify-start">
                <div className="max-w-[80%] rounded-2xl rounded-bl-sm bg-muted px-4 py-2 text-sm text-foreground">
                  <AssistantMarkdown text={entry.text} onNavigate={goTo} />
                </div>
              </div>
            );
          }
          if (entry.type === 'navlink') {
            return (
              <div key={entry.id} className="flex items-center gap-2 pl-1 text-xs text-muted-foreground">
                <span>Opened</span>
                <button
                  onClick={() => goTo(entry.path)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 font-medium text-primary hover:bg-muted transition-athletic"
                  title={entry.path}
                >
                  <Icon name="ExternalLink" size={12} />
                  <span>{entry.label}</span>
                </button>
              </div>
            );
          }
          if (entry.type === 'status') {
            const active = entry.id === activeStatusId;
            return (
              <div key={entry.id} className="flex items-center gap-2 pl-1 text-xs text-muted-foreground">
                <Icon
                  name={entry.cart ? 'ShoppingCart' : active ? 'Loader' : entry.icon || 'Check'}
                  size={12}
                  className={cn(active && 'animate-spin')}
                  data-status-state={entry.cart ? 'cart' : active ? 'active' : 'done'}
                />
                <span>{formatStatusLabel(entry)}</span>
                {/* Cart changes no longer pop the cart open over the chat —
                    the coach opens it from here when they want to look. */}
                {entry.cart && (
                  <button
                    type="button"
                    onClick={() => viewCart(entry.cart)}
                    className="font-medium text-primary hover:underline"
                  >
                    View cart
                  </button>
                )}
              </div>
            );
          }
          if (entry.type === 'restaurants') {
            return (
              <RestaurantCards key={entry.id} restaurants={entry.restaurants || []} onOpen={goTo} />
            );
          }
          if (entry.type === 'quote') {
            return (
              <QuoteCard
                key={entry.id}
                quotePayload={entry.quotePayload}
                dismissed={entry.dismissed}
                ordered={entry.ordered}
                onConfirm={() => confirmQuote(entry.quotePayload, entry.id)}
                onCancel={() => cancelQuote(entry.id)}
                onBuild={() => !sending && sendMessage('Build this as a cart.')}
                onViewOrders={() => goTo('/order-history-management')}
              />
            );
          }
          if (entry.type === 'proposal') {
            return (
              <ProposalCard
                key={entry.id}
                entry={entry}
                orgType={orgType}
                onConfirm={(choice) => confirmProposal(entry.id, choice)}
                onCancel={() => cancelProposal(entry.id)}
                onOpen={goTo}
              />
            );
          }
          if (entry.type === 'error') {
            return (
              <div key={entry.id} className="rounded-md border border-error/30 bg-error/10 px-3 py-2 text-sm text-error">
                {entry.label}
              </div>
            );
          }
          return null;
        })}
      </div>

      <form onSubmit={handleSubmit} className="flex items-end gap-2 border-t border-border p-3">
        <textarea
          ref={inputRef}
          aria-label="Message the meal assistant"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              handleSubmit();
            }
          }}
          rows={2}
          placeholder="What does your team need?"
          className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <Button type="submit" iconName="Send" loading={sending} disabled={!input.trim() || sending}>
          Send
        </Button>
      </form>
    </div>
  );
};

export default AssistantChat;
